import React from 'react';
import isEqual from 'react-fast-compare';
import { differenceInMinutes } from 'date-fns';
import { DirectionsRenderer, GoogleMap, Libraries, Marker, useLoadScript } from '@react-google-maps/api';
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { getGoogleMapDirections } from '../../api/locations';
import { GOOGLE_MAP_API_KEY } from '../../utils/constants';
import MarkerInfo from './MarkerInfo';
import { DrivingDistanceRoutesTransformed, LatLng, Location, SearchLocation } from '../../api/models/locations';
import PickupIconRawSvg from '../../assets/img/pickupMapGreenIconV2.svg';
import CareemMarkerLogoIconRawSvg from '../../assets/img/careemLogoCaptainIcon.svg';
import DropOffWhiteFlagIcon from '../../assets/img/dropOffMapWhiteIconV2.svg';
import DropOffGreenFlagIcon from '../../assets/img/dropOffMapGreenIconV2.svg';
import DropoffRevampedIconRawSvg from '../../assets/img/dropoffRevampedIcon.svg';
import DropoffRevampedIconSmallerRawSvg from '../../assets/img/dropoffRevampedIconSmaller.svg';
import PickupRevampedIconRawSvg from '../../assets/img/pickupRevampedIcon.svg';
import PickupRevampedIconSmallerRawSvg from '../../assets/img/pickupRevampedIconSmaller.svg';
import EmptyGreenIconRawSvg from '../../assets/img/emptyGreenIcon.svg';
import PinWithEtaMinIcon from '../../assets/img/pinWithEtaMinIcon.svg';
import CaptainArrivedAtPickup from '../../assets/img/captainArrivedAtPickup.svg';
import { createLatLngBounds } from '../../utils/google-maps';
import styles from './Map.module.less';
import classnames from 'classnames';
import { getDropOffLocationTitle, getPickupLocationTitle } from '../../utils/deliveries';

export interface LocationPoint {
  lat: number;
  lng: number;
}

export enum MapUseCase {
  CreatePickup = 'CreatePickup',
  RequestDelivery = 'RequestDelivery',
  ViewActiveDelivery = 'ViewActiveDelivery',
  SelectDropoff = 'SelectDropoff'
}

export enum DirectionType {
  PickupDropOff = 'PickupDropOff',
  DriverPickup = 'DriverPickup',
  DriverAtPickupNow = 'DriverAtPickupNow',
  DriverDropOff = 'DriverDropOff'
}

const mapLibraries: Libraries = ['geometry', 'drawing', 'places'];

const PickupDropOffIcons = {
  detailed: {
    normal: {
      pickup: PickupRevampedIconRawSvg,
      dropoff: DropoffRevampedIconRawSvg
    },
    smaller: {
      pickup: PickupRevampedIconSmallerRawSvg,
      dropoff: DropoffRevampedIconSmallerRawSvg
    }
  },
  common: {
    pickup: PickupIconRawSvg,
    dropoff: DropOffWhiteFlagIcon
  },
  useCases: {
    [MapUseCase.CreatePickup]: {
      dropoff: PickupIconRawSvg
    },
    [MapUseCase.SelectDropoff]: {
      dropoff: DropOffGreenFlagIcon
    },
    [MapUseCase.ViewActiveDelivery]: {
      [DirectionType.DriverPickup]: {
        pickup: PinWithEtaMinIcon,
        dropoff: DropOffWhiteFlagIcon
      },
      [DirectionType.DriverAtPickupNow]: {
        pickup: CaptainArrivedAtPickup,
        dropoff: DropOffWhiteFlagIcon
      },
      [DirectionType.DriverDropOff]: {
        pickup: PickupIconRawSvg,
        dropoff: PinWithEtaMinIcon
      }
    }
  }
};

export interface MapData {
  pickup?: Location;
  dropOff?: SearchLocation;
  driver?: { location?: LatLng };
  showDirections: boolean;
  showMarkerInfo: boolean;
  options: google.maps.MapOptions;
  center?: LatLng;
  directionType?: DirectionType;
  bookingId?: number | string;
  userId?: number;
}

export type DirectionsChange = {
  distance: number;
};

export interface ExternalProps {
  mapUseCase: MapUseCase;
  setTripDirections: (directions?: { duration: number; distance: number }) => void;
  resetEstimations: () => void;
  onDropOffMarkerChange?: (location: LocationPoint) => void;
  onDirectionsChange?: (directionsChange: DirectionsChange) => void;
  useExternalDirections?: boolean;
  externalDirections?: DrivingDistanceRoutesTransformed;
  containerClassName?: string;
  mapDefaultCenter?: LocationPoint;
  overlay?: React.ReactNode;
  mapBar?: React.ReactNode;
  useDetailedIcons?: React.ReactNode;
  driverEta?: number;
}

interface State {
  originLatLng?: any;
  destinationLatLng?: any;
  waypointsLatLng?: any[];
  directions: any;
  showPickupInfo: boolean;
  showDropOffInfo: boolean;
  showCaptainInfo: boolean;
  isDropOffDragged: boolean;
}

export type ComponentsProps = MapData & ExternalProps & WithTranslation;

export class Map extends React.Component<ComponentsProps, State> {
  static defaultProps = {
    showDirections: true,
    showMarkerInfo: true,
    directionType: DirectionType.PickupDropOff
  };
  mapRef: google.maps.Map | null = null;
  googleMaps: any;
  center: { lng: number; lat: number } | null = null;

  constructor(props: ComponentsProps) {
    super(props);

    this.googleMaps = window.google.maps;

    this.state = {
      directions: null,
      showPickupInfo: false,
      showDropOffInfo: false,
      showCaptainInfo: false,
      isDropOffDragged: false,
      ...this.getLatLngPoints(props)
    };
  }

  componentDidMount() {
    this.initMap();
  }

  componentDidUpdate(prevProps: MapData) {
    if (!isEqual(prevProps, this.props)) {
      this.initMap();
    }
  }

  getLatLngPoints(props: MapData) {
    const {
      pickup: { location: pickup } = {},
      dropOff: { location: dropOff } = {},
      driver: driverDetails = { location: undefined },
      center
    } = props;

    const driverLocation = driverDetails?.location;

    const pickupLatLng = pickup && new this.googleMaps.LatLng(pickup.lat, pickup.lng);
    const dropOffLatLng = dropOff && new this.googleMaps.LatLng(dropOff.lat, dropOff.lng);
    const driverLatLng = driverLocation && new this.googleMaps.LatLng(driverLocation.lat, driverLocation.lng);
    const centerLatLng = center && new this.googleMaps.LatLng(center.lat, center.lng);

    return {
      pickupLatLng,
      dropOffLatLng,
      driverLatLng,
      centerLatLng
    };
  }

  trimLocationName(name = '') {
    return name.length > 30 ? `${name.substring(0, 27)}...` : name;
  }

  getDirectionsPoints(): (LatLng | undefined)[] {
    const {
      directionType,
      pickup: { location: pickup } = {},
      dropOff: { location: dropOff } = {},
      driver: driverDetails = { location: undefined }
    } = this.props;
    let startPoint;
    let endPoint;
    let extraPoint;

    const driver = driverDetails?.location;

    switch (directionType) {
      case DirectionType.DriverDropOff:
        startPoint = driver;
        endPoint = dropOff;
        extraPoint = pickup;
        break;
      case DirectionType.DriverPickup:
        startPoint = driver;
        endPoint = pickup;
        extraPoint = dropOff;
        break;
      case DirectionType.PickupDropOff:
      default:
        startPoint = pickup;
        endPoint = dropOff;
        extraPoint = driver;
        break;
    }
    return [startPoint, endPoint, extraPoint];
  }

  initMap() {
    const { showDirections, bookingId, userId, externalDirections, useExternalDirections } = this.props;
    const [startPoint, endPoint, extraPoint] = this.getDirectionsPoints();
    if (useExternalDirections && !externalDirections) {
      this.setState({ directions: null });
    }
    if (!startPoint || !endPoint || !showDirections) {
      this.centerMap();
      return;
    }
    if (useExternalDirections) {
      if (extraPoint && externalDirections) {
        externalDirections.routes[0].bounds.extend(new this.googleMaps.LatLng(extraPoint.lat, extraPoint.lng));
      }
      this.setState({
        directions: externalDirections
      });
    } else {
      this.setState({
        directions: null
      });
      getGoogleMapDirections(startPoint, endPoint, { bookingId, userId })
        .then(directions => {
          if (extraPoint) {
            directions.routes[0].bounds.extend(new this.googleMaps.LatLng(extraPoint.lat, extraPoint.lng));
          }
          this.setState({
            directions
          });
          this.props?.onDirectionsChange?.({
            distance: directions.routes[0].distance
          });
        })
        .catch(() => {});
    }
  }

  onLoad = (map: any) => {
    this.mapRef = map;
    this.centerMap();
  };

  centerMap = () => {
    if (this.mapRef) {
      const { pickupLatLng, dropOffLatLng, driverLatLng } = this.getLatLngPoints(this.props);
      const bounds = createLatLngBounds();
      let count = 0;
      if (pickupLatLng) {
        count++;
        bounds.extend(pickupLatLng);
      }
      if (dropOffLatLng) {
        count++;
        bounds.extend(dropOffLatLng);
      }
      if (driverLatLng) {
        count++;
        bounds.extend(driverLatLng);
      }
      if (count > 1) {
        this.mapRef.fitBounds(bounds);
      }
      if (count > 0) {
        this.mapRef.setZoom(16);
        this.mapRef.setCenter(bounds.getCenter());
      } else {
        bounds.extend({ lat: 25.2048, lng: 55.2708 });
        this.mapRef.setCenter(bounds.getCenter());
      }
    }
  };

  togglePickupInfoVisibility = () => {
    if (!this.props.showMarkerInfo) {
      return;
    }
    this.setState({
      showPickupInfo: !this.state.showPickupInfo
    });
  };

  toggleDropOffInfoVisibility = () => {
    if (!this.props.showMarkerInfo) {
      return;
    }
    this.setState({
      showDropOffInfo: !this.state.showDropOffInfo
    });
  };

  toggleCaptainInfoVisibility = () => {
    if (!this.props.showMarkerInfo) {
      return;
    }
    this.setState({
      showCaptainInfo: !this.state.showCaptainInfo
    });
  };

  shouldComponentUpdate(prevProps: ComponentsProps, prevState: State) {
    return !isEqual(this.state, prevState) || !isEqual(this.props, prevProps);
  }

  getIcon(iconType: 'pickup' | 'dropoff', isDetailed: boolean, length = 0) {
    const { directionType, mapUseCase } = this.props;
    if (mapUseCase === MapUseCase.CreatePickup) {
      return PickupDropOffIcons.useCases[MapUseCase.CreatePickup].dropoff;
    } else if (mapUseCase === MapUseCase.SelectDropoff) {
      return PickupDropOffIcons.useCases[MapUseCase.SelectDropoff].dropoff;
    } else if (isDetailed) {
      return length > 15 ? PickupDropOffIcons.detailed.normal[iconType] : PickupDropOffIcons.detailed.smaller[iconType];
    } else if (directionType) {
      switch (directionType) {
        case DirectionType.DriverPickup:
          return PickupDropOffIcons.useCases[MapUseCase.ViewActiveDelivery][DirectionType.DriverPickup][iconType];
        case DirectionType.DriverAtPickupNow:
          return PickupDropOffIcons.useCases[MapUseCase.ViewActiveDelivery][DirectionType.DriverAtPickupNow][iconType];
        case DirectionType.DriverDropOff:
          return PickupDropOffIcons.useCases[MapUseCase.ViewActiveDelivery][DirectionType.DriverDropOff][iconType];
        default:
          return PickupDropOffIcons.common[iconType];
      }
    } else {
      return PickupDropOffIcons.common[iconType];
    }
  }

  render() {
    const { pickupLatLng, dropOffLatLng, driverLatLng, centerLatLng } = this.getLatLngPoints(this.props);
    const {
      pickup,
      dropOff,
      t,
      options,
      containerClassName,
      mapDefaultCenter,
      overlay,
      useDetailedIcons,
      directionType,
      driverEta,
      mapUseCase,
      mapBar
    } = this.props;
    const { isDropOffDragged } = this.state;
    const pickupLabel = pickup ? getPickupLocationTitle(pickup) : '';
    const dropOffLabel = isDropOffDragged ? ' ' : (dropOff && getDropOffLocationTitle(dropOff)) || '';
    const PickupIcon = this.getIcon(
      'pickup',
      Boolean(useDetailedIcons),
      pickupLabel.length
    ) as unknown as google.maps.Symbol;
    const DropoffIcon = isDropOffDragged
      ? EmptyGreenIconRawSvg
      : this.getIcon('dropoff', Boolean(useDetailedIcons), dropOffLabel.length);
    const { directions } = this.state;
    const mapCenter = this.center || pickupLatLng || dropOffLatLng || centerLatLng || mapDefaultCenter;

    return (
      <GoogleMap
        mapContainerClassName={classnames([styles.mapContainer, containerClassName])}
        zoom={16}
        center={mapCenter}
        onLoad={this.onLoad}
        onCenterChanged={() => {
          const center: any = this.mapRef?.getCenter();
          if (center) {
            this.center = {
              lat: center.lat(),
              lng: center.lng()
            };
          }
        }}
        options={{
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: mapUseCase === MapUseCase.SelectDropoff,
          zoomControlOptions: {
            // style: 1,
            position: 8
          },
          rotateControl: true,
          maxZoom: 20,
          minZoom: 10,
          ...options
        }}
      >
        {overlay}
        {mapBar}
        {directions && (
          <DirectionsRenderer
            options={{
              directions,
              polylineOptions: {
                strokeColor: '#37b44e'
              },
              suppressMarkers: true
            }}
          />
        )}

        {(pickupLatLng || dropOffLatLng) && (
          <>
            <Marker
              key="pickup"
              position={pickupLatLng}
              icon={PickupIcon}
              onClick={this.togglePickupInfoVisibility}
              label={
                useDetailedIcons
                  ? {
                      fontSize: '12px',
                      fontWeight: '600',
                      text: this.trimLocationName(pickupLabel)
                    }
                  : directionType === DirectionType.DriverPickup && driverEta
                    ? {
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        //@ts-ignore
                        className: 'marker-captain-eta-label',
                        text: `${differenceInMinutes(driverEta * 1000, 0) || 1}`
                      }
                    : {
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        //@ts-ignore
                        className: 'marker-captain-eta-label',
                        text: `--`
                      }
              }
              zIndex={9000}
            />
            {this.state.showPickupInfo ? (
              <MarkerInfo
                position={pickupLatLng}
                location={pickup?.nickname}
                title={t('general.pickup')}
                onCloseClick={this.togglePickupInfoVisibility}
              />
            ) : (
              ''
            )}
            {driverLatLng && (
              <Marker
                position={driverLatLng}
                icon={CareemMarkerLogoIconRawSvg}
                onClick={this.toggleCaptainInfoVisibility}
                zIndex={9002}
              />
            )}
            {this.state.showCaptainInfo ? (
              <MarkerInfo
                position={driverLatLng}
                title={t('general.driver')}
                onCloseClick={this.toggleCaptainInfoVisibility}
              />
            ) : (
              ''
            )}
            <Marker
              key="dropOff"
              position={dropOffLatLng}
              icon={DropoffIcon}
              draggable={Boolean(this.props.onDropOffMarkerChange)}
              onClick={this.toggleDropOffInfoVisibility}
              onDragStart={() =>
                this.setState({
                  isDropOffDragged: true
                })
              }
              onDragEnd={({ latLng: { lat, lng } }: any) => {
                this.setState({
                  isDropOffDragged: false
                });
                this.props.onDropOffMarkerChange?.({ lat: lat(), lng: lng() });
              }}
              label={
                useDetailedIcons
                  ? {
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'white',
                      text: this.trimLocationName(dropOffLabel)
                    }
                  : directionType === DirectionType.DriverDropOff && driverEta
                    ? {
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        //@ts-ignore
                        className: 'marker-captain-eta-label',
                        text: `${differenceInMinutes(driverEta * 1000, 0) || 1}`
                      }
                    : {
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        //@ts-ignore
                        className: 'marker-captain-eta-label',
                        text: `--`
                      }
              }
              zIndex={9001}
            />
            {this.state.showDropOffInfo ? (
              <MarkerInfo
                position={dropOffLatLng}
                location={dropOff?.formattedAddress}
                title={t('general.dropOff')}
                onCloseClick={this.toggleDropOffInfoVisibility}
              />
            ) : (
              ''
            )}
          </>
        )}
      </GoogleMap>
    );
  }
}

const MapLoader = (props: any) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_API_KEY || '',
    libraries: mapLibraries,
    version: '3.exp',
    language: 'en'
  });

  if (loadError) {
    return <Typography.Text>{t('deliveries.canNotLoadMap')}</Typography.Text>;
  }

  return isLoaded ? (
    <Map {...props} />
  ) : (
    <div className={styles.textContainer}>
      <Typography.Text>{t('deliveries.mapLoading')}</Typography.Text>
    </div>
  );
};

export default withTranslation()(MapLoader);
